import { useEffect, useMemo, useRef, useState } from 'react'
import { Printer, Save } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useParams, useSearchParams } from 'react-router-dom'
import { LmsPrintPreviewPdfButton, openLmsPrintPdfPreview } from '@/components/admin/print/LmsPrintPreviewPdfButton'
import { ChapterReportDocument } from '@/components/admin/chapter-report/ChapterReportDocument'
import type { SessionDraft } from '@/components/admin/chapter-report/SessionCommentEditor'
import {
  chapterReportErrorCode,
  isChapterReportConflict,
  useChapterReport,
  useResolveChapterReport,
  useUpdateChapterReport,
  useUpdateChapterReportAttendanceNote,
} from '@/hooks/coaching/useChapterReport'

function parsePositiveInt(value: string | undefined): number | null {
  if (!value) return null
  const parsed = Number(value)
  return Number.isInteger(parsed) && parsed > 0 ? parsed : null
}

function sameIds(left: number[], right: number[]): boolean {
  return left.length === right.length && left.every((value, index) => value === right[index])
}

function errorTranslationKey(error: unknown, fallback: string): string {
  const code = chapterReportErrorCode(error)
  return code ? `common:apiError.${code}` : fallback
}

export default function ChapterReportEditPage() {
  const { t } = useTranslation(['coaching', 'common'])
  const { learnerId: learnerIdRaw, testId: testIdRaw } = useParams<{
    learnerId: string
    testId: string
  }>()
  const [searchParams] = useSearchParams()
  const learnerId = parsePositiveInt(learnerIdRaw)
  const testId = parsePositiveInt(testIdRaw)
  const timeslotId = parsePositiveInt(searchParams.get('timeslotId') ?? undefined)

  if (learnerId == null || testId == null) {
    return <div className="p-8 text-red-600" role="alert">{t('coaching:chapterReport.invalidParams')}</div>
  }

  return <ChapterReportEditor learnerId={learnerId} testId={testId} timeslotId={timeslotId} />
}

function ChapterReportEditor({
  learnerId,
  testId,
  timeslotId,
}: {
  learnerId: number
  testId: number
  timeslotId: number | null
}) {
  const { t } = useTranslation(['coaching', 'common'])
  const resolveQuery = useResolveChapterReport(learnerId, testId)
  const reportId = resolveQuery.data?.reportId
  const reportQuery = useChapterReport(learnerId, reportId)
  const updateReport = useUpdateChapterReport(learnerId, reportId ?? 0)
  const updateNote = useUpdateChapterReportAttendanceNote(learnerId, reportId ?? 0)
  const initializedReportId = useRef<number | null>(null)
  const [overallComment, setOverallComment] = useState('')
  const [selectedLessonIds, setSelectedLessonIds] = useState<number[]>([])
  const [sessionDrafts, setSessionDrafts] = useState<Record<number, SessionDraft>>({})
  const [reportMessage, setReportMessage] = useState<{ tone: 'success' | 'error'; text: string } | null>(null)
  const [savingAttendanceId, setSavingAttendanceId] = useState<number | null>(null)

  const report = reportQuery.data
  useEffect(() => {
    if (!report || initializedReportId.current === report.reportId) return
    initializedReportId.current = report.reportId
    setOverallComment(report.overallComment ?? '')
    setSelectedLessonIds(report.recommendedLessonIds)
    setSessionDrafts(Object.fromEntries(
      report.classSessions.map((session) => [
        session.attendanceId,
        {
          note: session.coachingNote ?? '',
          version: session.coachingNoteVersion,
          error: null,
          saved: false,
        },
      ]),
    ))
  }, [report])

  const orderedSelectedLessonIds = useMemo(() => {
    if (!report) return []
    const selected = new Set(selectedLessonIds)
    return report.recommendationCandidates
      .filter((candidate) => selected.has(candidate.lessonId))
      .map((candidate) => candidate.lessonId)
  }, [report, selectedLessonIds])

  const isReportDirty = report != null && (
    overallComment !== (report.overallComment ?? '')
    || !sameIds(orderedSelectedLessonIds, report.recommendedLessonIds)
  )
  const hasDirtySession = report?.classSessions.some((session) =>
    (sessionDrafts[session.attendanceId]?.note ?? session.coachingNote ?? '') !== (session.coachingNote ?? ''),
  ) ?? false
  const isDirty = isReportDirty || hasDirtySession

  useEffect(() => {
    if (!isDirty) return
    const message = t('coaching:chapterReport.dirtyNavigation')
    const beforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault()
      event.returnValue = message
    }
    const blockAnchorNavigation = (event: MouseEvent) => {
      const target = event.target
      if (!(target instanceof Element) || !target.closest('a[href]')) return
      if (!window.confirm(message)) {
        event.preventDefault()
        event.stopPropagation()
      }
    }
    window.addEventListener('beforeunload', beforeUnload)
    document.addEventListener('click', blockAnchorNavigation, true)
    return () => {
      window.removeEventListener('beforeunload', beforeUnload)
      document.removeEventListener('click', blockAnchorNavigation, true)
    }
  }, [isDirty, t])

  const chapterReportContext = timeslotId == null ? '' : `?timeslotId=${timeslotId}`

  if (resolveQuery.isLoading || (reportId != null && reportQuery.isLoading)) {
    return <div className="p-8" role="status" aria-live="polite">{t('coaching:chapterReport.loading')}</div>
  }
  if (resolveQuery.error || reportQuery.error || !report) {
    const error = resolveQuery.error || reportQuery.error
    return (
      <div className="p-8" role="alert">
        <p className="text-red-600">
          {t(errorTranslationKey(error, 'coaching:chapterReport.loadError'))}
        </p>
        <button
          type="button"
          onClick={() => {
            resolveQuery.refetch()
            reportQuery.refetch()
          }}
          className="mt-3 rounded-lg border border-gray-300 px-3 py-2 text-sm"
        >
          {t('coaching:chapterReport.retry')}
        </button>
      </div>
    )
  }

  const saveReport = async () => {
    setReportMessage(null)
    try {
      const saved = await updateReport.mutateAsync({
        version: report.version,
        overallComment: overallComment.trim().length === 0 ? null : overallComment,
        recommendedLessonIds: orderedSelectedLessonIds,
      })
      setOverallComment(saved.overallComment ?? '')
      setSelectedLessonIds(saved.recommendedLessonIds)
      setReportMessage({ tone: 'success', text: t('coaching:chapterReport.saved') })
    } catch (error) {
      setReportMessage({
        tone: 'error',
        text: isChapterReportConflict(error)
          ? t('coaching:chapterReport.conflict')
          : t(errorTranslationKey(error, 'coaching:chapterReport.saveError')),
      })
    }
  }

  const saveSessionComment = async (attendanceId: number) => {
    const draft = sessionDrafts[attendanceId]
    if (!draft) return
    setSavingAttendanceId(attendanceId)
    setSessionDrafts((current) => ({
      ...current,
      [attendanceId]: { ...current[attendanceId], error: null, saved: false },
    }))
    try {
      const saved = await updateNote.mutateAsync({
        attendanceId,
        note: draft.note.trim().length === 0 ? null : draft.note,
        expectedVersion: draft.version,
      })
      setSessionDrafts((current) => ({
        ...current,
        [attendanceId]: {
          note: saved.note ?? '',
          version: saved.version,
          error: null,
          saved: true,
        },
      }))
    } catch (error) {
      setSessionDrafts((current) => ({
        ...current,
        [attendanceId]: {
          ...current[attendanceId],
          error: isChapterReportConflict(error)
            ? t('coaching:chapterReport.commentConflict')
            : t(errorTranslationKey(error, 'coaching:chapterReport.saveError')),
          saved: false,
        },
      }))
    } finally {
      setSavingAttendanceId(null)
    }
  }

  const openPrint = (downloadPdf = false) => {
    if (isDirty) {
      setReportMessage({ tone: 'error', text: t('coaching:chapterReport.dirtyPrint') })
      return
    }
    if (downloadPdf) {
      openLmsPrintPdfPreview(`/admin/coaching/chapter-report/${learnerId}/${testId}/print${chapterReportContext}`)
      return
    }
    window.open(
      `/admin/coaching/chapter-report/${learnerId}/${testId}/print${chapterReportContext}`,
      '_blank',
      'noopener,noreferrer',
    )
  }

  return (
    <div className="min-h-screen bg-[#f3f5f8] pb-12">
      <div className="sticky top-0 z-20 border-b border-gray-200 bg-white/95 px-6 py-3 backdrop-blur">
        <div className="mx-auto flex max-w-[1100px] flex-wrap items-center justify-between gap-3">
          <h1 className="font-bold text-gray-900">{t('coaching:chapterReport.editTitle')}</h1>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => openPrint()}
              className="inline-flex items-center gap-2 rounded-lg border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700"
            >
              <Printer size={16} aria-hidden="true" />
              {t('coaching:chapterReport.printPreview')}
            </button>
            <LmsPrintPreviewPdfButton onClick={() => openPrint(true)} />
            <button
              type="button"
              onClick={saveReport}
              disabled={!isReportDirty || updateReport.isPending}
              className="inline-flex items-center gap-2 rounded-lg bg-[#4599ff] px-4 py-2 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-40"
            >
              <Save size={16} aria-hidden="true" />
              {updateReport.isPending ? t('coaching:chapterReport.saving') : t('coaching:chapterReport.save')}
            </button>
          </div>
        </div>
        <p
          className={`mx-auto mt-2 min-h-5 max-w-[1100px] text-sm ${reportMessage?.tone === 'error' ? 'text-red-600' : 'text-[#2a9d68]'}`}
          aria-live="polite"
        >
          {reportMessage?.text ?? ''}
        </p>
      </div>

      <div className="mx-auto mt-8 max-w-[850px] overflow-hidden rounded-xl shadow-lg">
        <ChapterReportDocument
          report={report}
          editable
          selectedLessonIds={orderedSelectedLessonIds}
          overallComment={overallComment}
          sessionDrafts={sessionDrafts}
          savingAttendanceId={savingAttendanceId}
          onRecommendationsChange={(ids) => {
            setSelectedLessonIds(ids)
            setReportMessage(null)
          }}
          onOverallCommentChange={(comment) => {
            setOverallComment(comment)
            setReportMessage(null)
          }}
          onSessionDraftChange={(attendanceId, note) => {
            setSessionDrafts((current) => ({
              ...current,
              [attendanceId]: {
                ...current[attendanceId],
                note,
                error: null,
                saved: false,
              },
            }))
          }}
          onSessionSave={saveSessionComment}
        />
      </div>
    </div>
  )
}
